import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { TitleManagerService } from 'src/app/services/title-manager/title-manager.service';
import { TitleManagerModalService } from 'src/app/services/title-manager-modal/title-manager-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-title-manager',
  templateUrl: './add-edit-title-manager.component.html',
  styleUrls: ['./add-edit-title-manager.component.scss']
})
export class AddEditTitleManagerComponent extends DestructorComponent implements OnInit {

  @Output() titleManagerAdded = new EventEmitter<void>();
  @Output() titleManagerUpdated = new EventEmitter<void>();

  titleManagerForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedTitleManager: any = null;

  constructor(
    private fb: FormBuilder,
    private _titleManagerService: TitleManagerService,
    private _titleManagerModalService: TitleManagerModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.titleManagerForm = this.fb.group({
      link_file_name: ['', [Validators.required]],
      title: ['', [Validators.required]],
      description: [''],
      keywords: [''],
      show: ['show'],
      set_as_default: [false]
    });
  }

  // Getter methods for form controls
  get link_file_name() { return this.titleManagerForm.get('link_file_name'); }
  get title() { return this.titleManagerForm.get('title'); }
  get description() { return this.titleManagerForm.get('description'); }
  get keywords() { return this.titleManagerForm.get('keywords'); }
  get show() { return this.titleManagerForm.get('show'); }
  get set_as_default() { return this.titleManagerForm.get('set_as_default'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_title_manager_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceTitleManager = this._titleManagerModalService.getSelectedTitleManager();
        if (serviceTitleManager) {
          this.isEditMode = true;
          this.selectedTitleManager = serviceTitleManager;
          this.populateForm(this.selectedTitleManager);
        } else {
          this.isEditMode = false;
          this.selectedTitleManager = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._titleManagerModalService.clearSelectedTitleManager();
        this.resetForm();
      });
    }
  }

  private populateForm(titleManager: any): void {
    this.titleManagerForm.patchValue({
      link_file_name: titleManager.link_file_name || '',
      title: titleManager.title || '',
      description: titleManager.description || '',
      keywords: titleManager.keywords || '',
      show: titleManager.show ? 'show' : 'no_show',
      set_as_default: titleManager.set_as_default || false
    });
  }

  private resetForm(): void {
    this.titleManagerForm.reset({
      link_file_name: '',
      title: '',
      description: '',
      keywords: '',
      show: 'show',
      set_as_default: false
    });
    this.isLoading = false;
    this.markFormGroupUntouched(this.titleManagerForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_title_manager_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.titleManagerForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.titleManagerForm.value;

      // Prepare submit data
      const submitData: any = {
        link_file_name: formData.link_file_name,
        title: formData.title,
        description: formData.description || '',
        keywords: formData.keywords || '',
        show: formData.show === 'show',
        set_as_default: formData.set_as_default || false
      };

      const apiCall = this.isEditMode
        ? this._titleManagerService.UpdateData(this.selectedTitleManager._id, submitData)
        : this._titleManagerService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Title manager operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Title manager has been successfully updated!"
                : "Title manager has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.titleManagerUpdated.emit();
                } else {
                  this.titleManagerAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Title manager operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.titleManagerForm);
    }
  }
}
